// Trade notifications - pure change detection functions
// Port of public/notifications.js detectTradeChanges (lines 471-563)
// No DOM or framework dependencies - just data diffing

/**
 * Generate a unique key for a card (matches original implementation)
 * @param {Object} card - Card object with scryfallId and isFoil
 * @returns {string}
 */
export function cardKey(card) {
  return `${card.scryfallId}_${card.isFoil ? 'foil' : 'nonfoil'}`;
}

/**
 * Detect changes between two trade snapshots.
 * Used to show notifications for remote changes.
 *
 * @param {Object} oldTrade - Previous trade state
 * @param {Object} newTrade - Current trade state
 * @param {string} currentUserToken - Current user's token to identify own changes
 * @returns {Array} Array of change objects
 */
export function detectTradeChanges(oldTrade, newTrade, currentUserToken) {
  if (!oldTrade || !newTrade) return [];

  const changes = [];

  // Check acceptance changes
  if (oldTrade.acceptedByA !== newTrade.acceptedByA && newTrade.acceptedByA) {
    changes.push({ type: 'accepted', side: 'A', byCurrentUser: false });
  }
  if (oldTrade.acceptedByB !== newTrade.acceptedByB && newTrade.acceptedByB) {
    changes.push({ type: 'accepted', side: 'B', byCurrentUser: false });
  }

  // Check cancellation
  if (oldTrade.status !== 'cancelled' && newTrade.status === 'cancelled') {
    changes.push({
      type: 'cancelled',
      byCurrentUser: newTrade.cancelledBy === currentUserToken,
    });
  }

  // Check card changes for both sides
  ['sideA', 'sideB'].forEach((sideName) => {
    const oldCards = oldTrade[sideName]?.cards || [];
    const newCards = newTrade[sideName]?.cards || [];

    const oldCardMap = new Map(oldCards.map((c) => [cardKey(c), c]));
    const newCardMap = new Map(newCards.map((c) => [cardKey(c), c]));

    // Cards added
    newCards.forEach((card) => {
      const key = cardKey(card);
      if (!oldCardMap.has(key)) {
        changes.push({
          type: 'card_added',
          side: sideName,
          card: card,
          byCurrentUser: false,
        });
      }
    });

    // Cards removed
    oldCards.forEach((card) => {
      const key = cardKey(card);
      if (!newCardMap.has(key)) {
        changes.push({
          type: 'card_removed',
          side: sideName,
          card: card,
          byCurrentUser: false,
        });
      }
    });

    // Cards with quantity or status changes
    newCards.forEach((newCard) => {
      const key = cardKey(newCard);
      const oldCard = oldCardMap.get(key);
      if (oldCard) {
        if (oldCard.quantity !== newCard.quantity) {
          changes.push({
            type: 'quantity_changed',
            side: sideName,
            card: newCard,
            oldQuantity: oldCard.quantity,
            newQuantity: newCard.quantity,
            byCurrentUser: false,
          });
        }
        if (oldCard.status !== newCard.status) {
          changes.push({
            type: 'status_changed',
            side: sideName,
            card: newCard,
            oldStatus: oldCard.status,
            newStatus: newCard.status,
            byCurrentUser: false,
          });
        }
      }
    });
  });

  return changes;
}
